import tinycolor from 'tinycolor2';
import {colorMixKM, getLookUpTable} from './kubelkaMonk';
import {Point2D, SeededRandom} from './utils';
import {ChangeTextureSet, FingerTip, FlowCell, FlowCellMove, TipAction, TipApplication, TipInstrument, TipMotion} from './fingertip';
import { CrcOff, Page } from './page';
import { initialize } from 'workbox-google-analytics';
import { WorkingItem, GPUChecker, GPURunner, ProgramBuffers, ProgramInfo, UniformLocations } from './GPURunner';
import { PageChange, PageChangeEntry } from './PageChange';

export interface DryUniformLocations extends UniformLocations {
    factor: WebGLUniformLocation,
    pageAgeTexNum: WebGLUniformLocation,
    pageWetDepthTexNum: WebGLUniformLocation,
    pageDryDepthTexNum: WebGLUniformLocation,
    pageVisLatentCTexNum: WebGLUniformLocation,
    pageVisLatentMTexNum: WebGLUniformLocation,
    pageDryLatentCTexNum: WebGLUniformLocation,
    pageDryLatentMTexNum: WebGLUniformLocation,
    pageWetLatentCTexNum: WebGLUniformLocation,
    pageWetLatentMTexNum: WebGLUniformLocation,
};

export interface DryProgramInfo extends ProgramInfo {
    uniformLocations: DryUniformLocations
};

export interface DryProgramBuffers extends ProgramBuffers {

};

export class DryPageChange extends PageChange {
    
    static startupProgram: DryProgramInfo = {uniformLocations:{}} as DryProgramInfo;
    static processWetProgram: DryProgramInfo = {uniformLocations:{}} as DryProgramInfo;
    static processDryProgram: DryProgramInfo = {uniformLocations:{}} as DryProgramInfo;
    static finishProgram: DryProgramInfo = {uniformLocations:{}} as DryProgramInfo;

    static dryBuffers: DryProgramBuffers | undefined;

static fsSourceStartup = `#version 300 es
#line 40055
precision highp float;
precision highp int;
//precision highp uint;
in highp vec2 v_texCoordRendered;
in highp vec2 v_texCoordExternal;
uniform highp usampler2D u_pageAgeTexNum;
layout(location = 0) out highp uint outputAge;

void main() {
    outputAge = texture(u_pageAgeTexNum, v_texCoordRendered).r;
    if (outputAge > 1u) {
        outputAge --;
    }
}
`;

static fsSourceProcessWet = `#version 300 es
#line 40073
precision highp float;
precision highp int;
//precision highp uint;
uniform highp vec2 u_resolution;
uniform highp float u_factor;
in highp vec2 v_texCoordRendered;
in highp vec2 v_texCoordExternal;
uniform highp usampler2D u_pageAgeTexNum;
uniform highp usampler2D u_pageWetDepthTexNum;
uniform highp usampler2D u_pageWetLatentCTexNum;
uniform highp usampler2D u_pageWetLatentMTexNum;
layout(location = 0) out highp uint outputAge;
layout(location = 1) out highp uint outputWetDepth;
layout(location = 2) out highp uvec4 outputWetLatentC;
layout(location = 3) out highp uvec4 outputWetLatentM;
//highp uint paintDepthScale = 4u;
//highp uint maxCoronaDepth = 256u * paintDepthScale;
highp uint maxPaintDepth = 10u * 256u * 4u; 

void main() {
    highp uint inputAge = texture(u_pageAgeTexNum, v_texCoordRendered).r;
    outputAge = inputAge;
    outputWetDepth = texture(u_pageWetDepthTexNum, v_texCoordRendered).r;
    outputWetLatentC = texture(u_pageWetLatentCTexNum, v_texCoordRendered);
    outputWetLatentM = texture(u_pageWetLatentMTexNum, v_texCoordRendered);
    if (inputAge == 1u) {
        highp uint finalDepth = outputWetDepth;
        if (finalDepth > maxPaintDepth) {
            finalDepth = maxPaintDepth;
        }
        highp uint reduce = max(1u, uint(floor(float(finalDepth) * u_factor)));
        if (reduce > finalDepth) {
            reduce = finalDepth;
            finalDepth = 0u;
        } else {
            finalDepth -= reduce;
        }
        outputWetDepth = finalDepth;
        if (finalDepth == 0u) {
            outputWetLatentC = uvec4(0u, 0u, 0u, 0u);
            outputWetLatentM = uvec4(0u, 0u, 0u, 1u);

            outputAge = 0u;
        }
    }
}
`;
static fsSourceProcessDry = `#version 300 es
#line 40122
precision highp float;
precision highp int;
//precision highp uint;
uniform highp vec2 u_resolution;
uniform highp float u_factor;
in highp vec2 v_texCoordRendered;
in highp vec2 v_texCoordExternal;
uniform highp usampler2D u_pageAgeTexNum;
uniform highp usampler2D u_pageWetDepthTexNum;
uniform highp usampler2D u_pageDryDepthTexNum;
uniform highp usampler2D u_pageDryLatentCTexNum;
uniform highp usampler2D u_pageDryLatentMTexNum;
uniform highp usampler2D u_pageVisLatentCTexNum;
uniform highp usampler2D u_pageVisLatentMTexNum;
layout(location = 0) out highp uvec4 outputDryLatentC;
layout(location = 1) out highp uvec4 outputDryLatentM;
layout(location = 2) out highp uint outputDryDepth;
//highp uint paintDepthScale = 4u;
//highp uint maxCoronaDepth = 256u * paintDepthScale;
highp uint maxPaintDepth = 10u * 256u * 4u; 

void main() {
    highp uint inputAge = texture(u_pageAgeTexNum, v_texCoordRendered).r;
    outputDryDepth = texture(u_pageDryDepthTexNum, v_texCoordRendered).r;
    outputDryLatentC = texture(u_pageDryLatentCTexNum, v_texCoordRendered);
    outputDryLatentM = texture(u_pageDryLatentMTexNum, v_texCoordRendered);
    if (inputAge == 1u) {
        outputDryLatentC = texture(u_pageVisLatentCTexNum, v_texCoordRendered);
        outputDryLatentM = texture(u_pageVisLatentMTexNum, v_texCoordRendered);
        highp uint finalDepth = texture(u_pageWetDepthTexNum, v_texCoordRendered).r;;
        if (finalDepth > maxPaintDepth) {
            finalDepth = maxPaintDepth;
        }
        highp uint reduce = max(1u, uint(floor(float(finalDepth) * u_factor)));
        if (reduce > finalDepth) {
            reduce = finalDepth;
            finalDepth = 0u;
        } else {
            finalDepth -= reduce;
        }
        outputDryDepth += reduce;
        if (outputDryDepth > maxPaintDepth) {
            outputDryDepth = maxPaintDepth;
        }
    }
}
`;


    static getPrograms(): Array<[string, ProgramInfo]> {
        //return [[DryPageChange.fsSourceInit, DryPageChange.initProgram], [DryPageChange.fsSourceProcess, DryPageChange.processProgram], [DryPageChange.fsSourceFinish, DryPageChange.finishProgram]];
        return [[DryPageChange.fsSourceStartup, DryPageChange.startupProgram], [DryPageChange.fsSourceProcessWet, DryPageChange.processWetProgram], [DryPageChange.fsSourceProcessDry, DryPageChange.processDryProgram]];
    }

    static initProgramLocations(gcFrom: GPUChecker) {
        const gc = new GPUChecker(gcFrom.gl, gcFrom, 'dry-initProgramLocations');
        const gl = gc.gl;

        DryPageChange.startupProgram.uniformLocations = {} as DryUniformLocations;
        gc.check('1', ()=>DryPageChange.addStartupProgramLocations(gc, DryPageChange.startupProgram));

        DryPageChange.processWetProgram.uniformLocations = {} as DryUniformLocations;
        gc.check('2', ()=>DryPageChange.addProcessWetProgramLocations(gc, DryPageChange.processWetProgram));

        DryPageChange.processDryProgram.uniformLocations = {} as DryUniformLocations;
        gc.check('3', ()=>DryPageChange.addProcessDryProgramLocations(gc, DryPageChange.processDryProgram));

        DryPageChange.finishProgram.uniformLocations = {} as DryUniformLocations;
        gc.check('4', ()=>DryPageChange.addFinishProgramLocations(gc, DryPageChange.finishProgram));

    }

    static addStartupProgramLocations(gcFrom: GPUChecker, programInfo: DryProgramInfo) {
        const gc = new GPUChecker(gcFrom.gl, gcFrom, 'dry-addStartupProgramLocations');
        const gl = gc.gl;

        gc.checkAPI('1', ()=>programInfo.uniformLocations.pageAgeTexNum = gl.getUniformLocation(programInfo.program, 'u_pageAgeTexNum')!, true);
        gc.check('2', ()=>PageChange.addProgramLocations(gc, programInfo, true));
    }

    static addProcessWetProgramLocations(gcFrom: GPUChecker, programInfo: DryProgramInfo) {
        const gc = new GPUChecker(gcFrom.gl, gcFrom, 'dry-addProcessWetProgramLocations');
        const gl = gc.gl;

        gc.checkAPI('1', ()=>programInfo.uniformLocations.pageAgeTexNum = gl.getUniformLocation(programInfo.program, 'u_pageAgeTexNum')!, true);
        gc.checkAPI('2', ()=>programInfo.uniformLocations.pageWetDepthTexNum = gl.getUniformLocation(programInfo.program, 'u_pageWetDepthTexNum')!, true);
        gc.checkAPI('3', ()=>programInfo.uniformLocations.pageWetLatentCTexNum = gl.getUniformLocation(programInfo.program, 'u_pageWetLatentCTexNum')!, true);
        gc.checkAPI('4', ()=>programInfo.uniformLocations.pageWetLatentMTexNum = gl.getUniformLocation(programInfo.program, 'u_pageWetLatentMTexNum')!, true);

        gc.checkAPI('10', ()=>programInfo.uniformLocations.factor = gl.getUniformLocation(programInfo.program, 'u_factor')!, true);

        gc.check('11', ()=>PageChange.addProgramLocations(gc, programInfo, true));
    }
    static addProcessDryProgramLocations(gcFrom: GPUChecker, programInfo: DryProgramInfo) {
        const gc = new GPUChecker(gcFrom.gl, gcFrom, 'dry-addProcessDryProgramLocations');
        const gl = gc.gl;

        gc.checkAPI('1', ()=>programInfo.uniformLocations.pageAgeTexNum = gl.getUniformLocation(programInfo.program, 'u_pageAgeTexNum')!, true);
        gc.checkAPI('2', ()=>programInfo.uniformLocations.pageWetDepthTexNum = gl.getUniformLocation(programInfo.program, 'u_pageWetDepthTexNum')!, true);
        gc.checkAPI('5', ()=>programInfo.uniformLocations.pageDryDepthTexNum = gl.getUniformLocation(programInfo.program, 'u_pageDryDepthTexNum')!, true);
        gc.checkAPI('6', ()=>programInfo.uniformLocations.pageDryLatentCTexNum = gl.getUniformLocation(programInfo.program, 'u_pageDryLatentCTexNum')!, true);
        gc.checkAPI('7', ()=>programInfo.uniformLocations.pageDryLatentMTexNum = gl.getUniformLocation(programInfo.program, 'u_pageDryLatentMTexNum')!, true);
        gc.checkAPI('8', ()=>programInfo.uniformLocations.pageVisLatentCTexNum = gl.getUniformLocation(programInfo.program, 'u_pageVisLatentCTexNum')!, true);
        gc.checkAPI('9', ()=>programInfo.uniformLocations.pageVisLatentMTexNum = gl.getUniformLocation(programInfo.program, 'u_pageVisLatentMTexNum')!, true);

        gc.checkAPI('10', ()=>programInfo.uniformLocations.factor = gl.getUniformLocation(programInfo.program, 'u_factor')!, true);

        gc.check('11', ()=>PageChange.addProgramLocations(gc, programInfo, true));
    }

    static addFinishProgramLocations(gcFrom: GPUChecker, programInfo: DryProgramInfo) {
        // programInfo.uniformLocations.pageAgeTexNum = gl.getUniformLocation(programInfo.program, 'u_pageAgeTexNum')!;

        // PageChange.addProgramLocations(gl, programInfo);
    }

    static initPermanentStorage(gcFrom: GPUChecker, textureCallback: (radius:number, application: TipApplication) => ChangeTextureSet) {
        const gc = new GPUChecker(gcFrom.gl, gcFrom, 'dry-initPermanentStorage');
        const gl = gc.gl;

        DryPageChange.dryBuffers = {} as DryProgramBuffers;
        gc.check('14', ()=>DryPageChange.addPermanentStorage(gc, DryPageChange.dryBuffers!, textureCallback));
    }
    static addPermanentStorage(gcFrom: GPUChecker, buffers: DryProgramBuffers, textureCallback: (radius:number, application: TipApplication) => ChangeTextureSet) {
        const gc = new GPUChecker(gcFrom.gl, gcFrom, 'dry-pass');
        PageChange.addPermanentStorage(gc, buffers, textureCallback);
        if (DryPageChange.dryBuffers! === undefined) {
        }

        //buffers.kmLookupTexture = DryPageChange.dryBuffers!.kmLookupTexture;
    }

    static wetAgeTexNum = 20;
    static wetDepthTexNum = 21;
    static dryDepthTexNum = 22;
    static dryLatentCTexNum = 23;
    static dryLatentMTexNum = 24;
    static wetLatentCTexNum = 25;
    static wetLatentMTexNum = 26;
    static visLatentCTexNum = 27;
    static visLatentMTexNum = 28;

    factor: number;

    decrementedAgeTexture: WebGLTexture | null = null;
    decrementedAgeFramebuffer: WebGLFramebuffer | null = null;
    finalWetAgeTexture: WebGLTexture | null = null;
    finalWetDepthTexture: WebGLTexture | null = null;
    finalDryDepthTexture: WebGLTexture | null = null;
    finalDryLatentCTexture: WebGLTexture | null = null;
    finalDryLatentMTexture: WebGLTexture | null = null;
    finalWetLatentCTexture: WebGLTexture | null = null;
    finalWetLatentMTexture: WebGLTexture | null = null;
    finalWetAgeColorDepthFramebuffer: WebGLFramebuffer | null = null;
    finalDryColorDepthFramebuffer: WebGLFramebuffer | null = null;


    constructor(page: Page, factor: number) {
        super(page, 0, 0, page.cellsWidth, page.cellsHeight);
        this.factor = factor;
    }
    override setViewport(): void {
        // set the resolution
        let gl = GPURunner.gl!;
        let width = this.page.cellsWidth;
        let height = this.page.cellsHeight;
        gl.canvas.width = width;
        gl.canvas.height = height;
        gl.viewport(0, 0, width, height);
    }
    override preparePageForStartup(gcFrom: GPUChecker): void {
        const gc = new GPUChecker(gcFrom.gl, gcFrom, 'dry-pass');
        GPURunner.initDryPageChange(gc, this.page, this);
    }
    override applyStartupToPage(gcFrom: GPUChecker): void {
    }
    override preparePageForProcess(gcFrom: GPUChecker): void {
    }
    override applyProcessToPage(gcFrom: GPUChecker): void {
    }
    override preparePageForFinish(gcFrom: GPUChecker): void {
    }
    override applyFinishToPage(gcFrom: GPUChecker): void {
        const gc = new GPUChecker(gcFrom.gl, gcFrom, 'dry-pass');
        GPURunner.applyDryPageChange(gc, this.page, this);
    }

    override selectStartupProgram(gcFrom: GPUChecker): DryProgramInfo {
        let gl = GPURunner.gl!;
        let pi = DryPageChange.startupProgram;

        gcFrom.checkAPI('dry-ssp', ()=>gl.useProgram(pi.program));
        return pi;
    }

    override initializeStartupProgram(gcFrom: GPUChecker, pi: DryProgramInfo) {
        let gl = GPURunner.gl!;
        let buffers = DryPageChange.dryBuffers!;

        if (GPURunner.doInitializeProgram(pi, 'dry')) {
            gcFrom.checkAPI('dry-isp', ()=>gl.uniform1i(pi.uniformLocations.pageAgeTexNum, DryPageChange.wetAgeTexNum));
        }
    }
    override specializeStartupRoutine(gcFrom: GPUChecker, pi: DryProgramInfo) {
        const gc = new GPUChecker(gcFrom.gl, gcFrom, 'dry-specializeStartupRoutine');
        const gl = gc.gl;
        let buffers = DryPageChange.dryBuffers!;

        // set the resolution
        let width = this.page.cellsWidth;
        let height = this.page.cellsHeight;

        let working = gc.check('1', ()=>GPURunner.workingFrameFactory.getItem(gc, 'dryDecAge', this.width, this.height, [
            {tag:'age', internalFormat:gl.R8UI, format: gl.RED_INTEGER, type:gl.UNSIGNED_BYTE}
        ])) as WorkingItem;

        this.decrementedAgeFramebuffer = working.fb;
        this.decrementedAgeTexture = working.tex0;
    }

    override runStartupRoutine(gcFrom: GPUChecker, pi: DryProgramInfo): void {
        const gc = new GPUChecker(gcFrom.gl, gcFrom, 'dry-runStartupRoutine');
        const gl = gc.gl;
        let buffers = DryPageChange.dryBuffers!;
        gc.check('1', ()=>GPURunner.activateTexture(gc, DryPageChange.wetAgeTexNum, this.pageWetAgeTexture));
        gc.checkAPI('3', ()=>gl.bindFramebuffer(gl.FRAMEBUFFER, this.decrementedAgeFramebuffer));
        gc.checkFB('pre4');
        gc.checkAPI('4', ()=>gl.drawArrays(gl.TRIANGLES, 0, PageChange.verts));
        gl.bindFramebuffer(gl.FRAMEBUFFER, null);

    }

    override finalizeStartup(gcFrom: GPUChecker, pi: DryProgramInfo): void {
    }

    override selectProcessProgram(gcFrom: GPUChecker): DryProgramInfo {
        let gl = GPURunner.gl!;
        let pi = DryPageChange.processWetProgram;

        gcFrom.checkAPI('dry-ssp', ()=>gl.useProgram(pi.program));
        return pi;
    }

    override initializeProcessProgram(gcFrom: GPUChecker, pi: DryProgramInfo): void {
        const gc = new GPUChecker(gcFrom.gl, gcFrom, 'dry-initializeProcessProgram');
        const gl = gc.gl;
        let buffers = DryPageChange.dryBuffers!;

        if (GPURunner.doInitializeProgram(pi, 'dry-wet')) {
            gc.checkAPI('1', ()=>gl.uniform1i(pi.uniformLocations.pageAgeTexNum, DryPageChange.wetAgeTexNum));
            gc.checkAPI('2', ()=>gl.uniform1i(pi.uniformLocations.pageWetDepthTexNum, DryPageChange.wetDepthTexNum));
            gc.checkAPI('3', ()=>gl.uniform1i(pi.uniformLocations.pageWetLatentCTexNum, DryPageChange.wetLatentCTexNum));
            gc.checkAPI('4', ()=>gl.uniform1i(pi.uniformLocations.pageWetLatentMTexNum, DryPageChange.wetLatentMTexNum));
            gc.checkAPI('10', ()=>gl.uniform1f(pi.uniformLocations.factor, this.factor));
        }
    }
    initializeProcessDryProgram(gcFrom: GPUChecker, pi: DryProgramInfo): void {
        const gc = new GPUChecker(gcFrom.gl, gcFrom, 'dry-initializeProcessDryProgram');
        const gl = gc.gl;
        let buffers = DryPageChange.dryBuffers!;

        if (GPURunner.doInitializeProgram(pi, 'dry-dry')) {
            gc.checkAPI('1', ()=>gl.uniform1i(pi.uniformLocations.pageAgeTexNum, DryPageChange.wetAgeTexNum));
            gc.checkAPI('2', ()=>gl.uniform1i(pi.uniformLocations.pageWetDepthTexNum, DryPageChange.wetDepthTexNum));
            gc.checkAPI('5', ()=>gl.uniform1i(pi.uniformLocations.pageDryDepthTexNum, DryPageChange.dryDepthTexNum));
            gc.checkAPI('6', ()=>gl.uniform1i(pi.uniformLocations.pageDryLatentCTexNum, DryPageChange.dryLatentCTexNum));
            gc.checkAPI('7', ()=>gl.uniform1i(pi.uniformLocations.pageDryLatentMTexNum, DryPageChange.dryLatentMTexNum));
            gc.checkAPI('8', ()=>gl.uniform1i(pi.uniformLocations.pageVisLatentCTexNum, DryPageChange.visLatentCTexNum));
            gc.checkAPI('9', ()=>gl.uniform1i(pi.uniformLocations.pageVisLatentMTexNum, DryPageChange.visLatentMTexNum));
            gc.checkAPI('10', ()=>gl.uniform1f(pi.uniformLocations.factor, this.factor));
        }
    }

    override specializeProcessRoutine(gcFrom: GPUChecker, pi: DryProgramInfo): void {
        const gc = new GPUChecker(gcFrom.gl, gcFrom, 'dry-specializeProcessRoutine');
        const gl = gc.gl;
        let buffers = DryPageChange.dryBuffers!;

        let width = this.page.cellsWidth;
        let height = this.page.cellsHeight;

        let working = gc.check('3', ()=>GPURunner.workingFrameFactory.getItem(gc, 'dryFinalWetALat', this.width, this.height, [
            {tag:'wetA', internalFormat:gl.R8UI, format: gl.RED_INTEGER, type:gl.UNSIGNED_BYTE},
            {tag:'wetD', internalFormat:gl.R16UI, format: gl.RED_INTEGER, type:gl.UNSIGNED_SHORT},
            {tag:'wetLC', internalFormat:gl.RGBA16UI, format: gl.RGBA_INTEGER, type:gl.UNSIGNED_SHORT},
            {tag:'wetLM', internalFormat:gl.RGBA16UI, format: gl.RGBA_INTEGER, type:gl.UNSIGNED_SHORT},
        ])) as WorkingItem;

        this.finalWetAgeColorDepthFramebuffer = working.fb;
        this.finalWetAgeTexture = working.tex0;
        this.finalWetDepthTexture = working.tex1;
        this.finalWetLatentCTexture = working.tex2;
        this.finalWetLatentMTexture = working.tex3;

        working = gc.check('3', ()=>GPURunner.workingFrameFactory.getItem(gc, 'dryFinalDryLatD', this.width, this.height, [
            {tag:'dryLC', internalFormat:gl.RGBA16UI, format: gl.RGBA_INTEGER, type:gl.UNSIGNED_SHORT},
            {tag:'dryLM', internalFormat:gl.RGBA16UI, format: gl.RGBA_INTEGER, type:gl.UNSIGNED_SHORT},
            {tag:'dryD', internalFormat:gl.R16UI, format: gl.RED_INTEGER, type:gl.UNSIGNED_SHORT},
        ])) as WorkingItem;

        this.finalDryColorDepthFramebuffer = working.fb;
        this.finalDryLatentCTexture = working.tex0;
        this.finalDryLatentMTexture = working.tex1;
        this.finalDryDepthTexture = working.tex2;

    }

    debugMeasureDepthTexture(text: WebGLTexture): [number, Uint16Array] {
        const gl = GPURunner.gl!;
        let sampFb = gl.createFramebuffer();
        gl.bindFramebuffer(gl.FRAMEBUFFER, sampFb);

        gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, text, 0);
        let texDepth = new Uint16Array(this.width * this.height);
        gl.readPixels(0, 0, this.width, this.height, gl.RED_INTEGER, gl.UNSIGNED_SHORT, texDepth);
        let d = 0;
        (texDepth.values() as any).forEach((element: number) => { d+=element; });
        return [d, texDepth];
    }


    override runProcessRoutine(gcFrom: GPUChecker, pi: DryProgramInfo): void {
        const gc = new GPUChecker(gcFrom.gl, gcFrom, 'dry-runProcessRoutine');
        const gl = gc.gl;
        let buffers = DryPageChange.dryBuffers!;

        gc.checkAPI('1', ()=>gl.bindFramebuffer(gl.FRAMEBUFFER, this.finalWetAgeColorDepthFramebuffer));
        gc.check('2', ()=>GPURunner.activateTexture(gc, DryPageChange.wetAgeTexNum, this.decrementedAgeTexture));
        gc.check('3', ()=>GPURunner.activateTexture(gc, DryPageChange.wetDepthTexNum, this.pageWetDepthTexture));
        gc.check('4', ()=>GPURunner.activateTexture(gc, DryPageChange.wetLatentCTexNum, this.pageWetLatentCTexture));
        gc.check('5', ()=>GPURunner.activateTexture(gc, DryPageChange.wetLatentMTexNum, this.pageWetLatentMTexture));

        gc.checkAPI('10', ()=>gl.uniform1f(pi.uniformLocations.factor, this.factor));
        gc.checkFB('pre21');
        //GPURunner.workingFrameFactory.activeTextures.forEach((t, k) => {console.log('active', k, (t as any).tag);});
        gc.checkAPI('21', ()=>gl.drawArrays(gl.TRIANGLES, 0, PageChange.verts));

        //let d= this.debugMeasureDepthTexture(this.finalWetDepthTexture!);
        //console.log('finalWetDepthTexture', d[0]);

        pi = DryPageChange.processDryProgram;
        gc.checkAPI('22', ()=>gl.useProgram(pi.program));
        gc.check('23', ()=>this.initializeProcessDryProgram(gc, pi));
        gc.checkAPI('24', ()=>gl.bindFramebuffer(gl.FRAMEBUFFER, this.finalDryColorDepthFramebuffer));
        gc.check('25', ()=>GPURunner.activateTexture(gc, DryPageChange.wetAgeTexNum, this.decrementedAgeTexture));
        gc.check('26', ()=>GPURunner.activateTexture(gc, DryPageChange.wetDepthTexNum, this.pageWetDepthTexture));
        gc.check('27', ()=>GPURunner.activateTexture(gc, DryPageChange.dryDepthTexNum, this.pageDryDepthTexture));
        gc.check('28', ()=>GPURunner.activateTexture(gc, DryPageChange.dryLatentCTexNum, this.pageDryLatentCTexture));
        gc.check('29', ()=>GPURunner.activateTexture(gc, DryPageChange.dryLatentMTexNum, this.pageDryLatentMTexture));
        gc.check('30', ()=>GPURunner.activateTexture(gc, DryPageChange.visLatentCTexNum, this.pageVisibleLatentCTexture));
        gc.check('31', ()=>GPURunner.activateTexture(gc, DryPageChange.visLatentMTexNum, this.pageVisibleLatentMTexture));

        gc.checkAPI('32', ()=>gl.uniform1f(pi.uniformLocations.factor, this.factor));
        gc.checkFB('pre43');
        //GPURunner.workingFrameFactory.activeTextures.forEach((t, k) => {console.log('active', k, (t as any).tag);});
        gc.checkAPI('43', ()=>gl.drawArrays(gl.TRIANGLES, 0, PageChange.verts));

        let blitReadX0 = 0;
        let blitReadY0 = 0;
        let blitReadX1 = this.page.cellsWidth;
        let blitReadY1 = this.page.cellsHeight;
        let blitDrawX0 = 0;
        let blitDrawY0 = 0;
        let blitDrawX1 = this.page.cellsWidth;
        let blitDrawY1 = this.page.cellsHeight;

        // put the combined age into the buffer
        gc.checkAPI('44', ()=>gl.bindFramebuffer(gl.READ_FRAMEBUFFER, this.finalWetAgeColorDepthFramebuffer));
        gc.checkAPI('45', ()=>gl.bindFramebuffer(gl.DRAW_FRAMEBUFFER, this.pageWetAgeFramebufferW));
        gc.checkAPI('46', ()=>gl.blitFramebuffer(blitReadX0, blitReadY0, blitReadX1, blitReadY1, blitDrawX0, blitDrawY0, blitDrawX1, blitDrawY1, gl.COLOR_BUFFER_BIT, gl.NEAREST));

        let fbTemp = gc.checkAPI('53', ()=>gl.createFramebuffer(), true) as WebGLFramebuffer;

        gc.checkAPI('47', ()=>gl.bindFramebuffer(gl.READ_FRAMEBUFFER, fbTemp));
        gc.checkAPI('55', ()=>gl.framebufferTexture2D(gl.READ_FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, this.finalWetDepthTexture, 0));
        gc.checkAPI('48', ()=>gl.bindFramebuffer(gl.DRAW_FRAMEBUFFER, this.pageWetDepthFramebufferW));
        gc.checkAPI('49', ()=>gl.blitFramebuffer(blitReadX0, blitReadY0, blitReadX1, blitReadY1, blitDrawX0, blitDrawY0, blitDrawX1, blitDrawY1, gl.COLOR_BUFFER_BIT, gl.NEAREST));

        gc.checkAPI('50', ()=>gl.bindFramebuffer(gl.READ_FRAMEBUFFER, fbTemp));
        gc.checkAPI('55', ()=>gl.framebufferTexture2D(gl.READ_FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, this.finalDryDepthTexture, 0));
        gc.checkAPI('51', ()=>gl.bindFramebuffer(gl.DRAW_FRAMEBUFFER, this.pageDryDepthFramebufferW));
        gc.checkAPI('52', ()=>gl.blitFramebuffer(blitReadX0, blitReadY0, blitReadX1, blitReadY1, blitDrawX0, blitDrawY0, blitDrawX1, blitDrawY1, gl.COLOR_BUFFER_BIT, gl.NEAREST));

        gc.checkAPI('54', ()=>gl.bindFramebuffer(gl.READ_FRAMEBUFFER, fbTemp));
        gc.checkAPI('55', ()=>gl.framebufferTexture2D(gl.READ_FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, this.finalWetLatentCTexture, 0));
        gc.checkAPI('56', ()=>gl.bindFramebuffer(gl.DRAW_FRAMEBUFFER, this.pageWetLatentCFramebufferW));
        gc.checkAPI('57', ()=>gl.blitFramebuffer(blitReadX0, blitReadY0, blitReadX1, blitReadY1, blitDrawX0, blitDrawY0, blitDrawX1, blitDrawY1, gl.COLOR_BUFFER_BIT, gl.NEAREST));

        gc.checkAPI('58', ()=>gl.bindFramebuffer(gl.READ_FRAMEBUFFER, fbTemp));
        gc.checkAPI('59', ()=>gl.framebufferTexture2D(gl.READ_FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, this.finalWetLatentMTexture, 0));
        gc.checkAPI('60', ()=>gl.bindFramebuffer(gl.DRAW_FRAMEBUFFER, this.pageWetLatentMFramebufferW));
        gc.checkAPI('61', ()=>gl.blitFramebuffer(blitReadX0, blitReadY0, blitReadX1, blitReadY1, blitDrawX0, blitDrawY0, blitDrawX1, blitDrawY1, gl.COLOR_BUFFER_BIT, gl.NEAREST));

        gc.checkAPI('62', ()=>gl.bindFramebuffer(gl.READ_FRAMEBUFFER, fbTemp));
        gc.checkAPI('63', ()=>gl.framebufferTexture2D(gl.READ_FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, this.finalDryLatentCTexture, 0));
        gc.checkAPI('64', ()=>gl.bindFramebuffer(gl.DRAW_FRAMEBUFFER, this.pageDryLatentCFramebufferW));
        gc.checkAPI('65', ()=>gl.blitFramebuffer(blitReadX0, blitReadY0, blitReadX1, blitReadY1, blitDrawX0, blitDrawY0, blitDrawX1, blitDrawY1, gl.COLOR_BUFFER_BIT, gl.NEAREST));

        gc.checkAPI('66', ()=>gl.bindFramebuffer(gl.READ_FRAMEBUFFER, fbTemp));
        gc.checkAPI('67', ()=>gl.framebufferTexture2D(gl.READ_FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, this.finalDryLatentMTexture, 0));
        gc.checkAPI('68', ()=>gl.bindFramebuffer(gl.DRAW_FRAMEBUFFER, this.pageDryLatentMFramebufferW));
        gc.checkAPI('69', ()=>gl.blitFramebuffer(blitReadX0, blitReadY0, blitReadX1, blitReadY1, blitDrawX0, blitDrawY0, blitDrawX1, blitDrawY1, gl.COLOR_BUFFER_BIT, gl.NEAREST));


        gl.bindFramebuffer(gl.FRAMEBUFFER, null);
        gl.deleteFramebuffer(fbTemp);
       

    }

    override finalizeProcess(gcFrom: GPUChecker, pi: DryProgramInfo): void {
    }

    override selectFinishProgram(gcFrom: GPUChecker): DryProgramInfo {
        return undefined as any as DryProgramInfo;
    }

    override initializeFinishProgram(gcFrom: GPUChecker, pi: DryProgramInfo): void {
    }
    override specializeFinishRoutine(gcFrom: GPUChecker, pi: DryProgramInfo): void {
    }
    override runFinishRoutine(gcFrom: GPUChecker, pi: DryProgramInfo): void {
    }
    override finalizeFinish(gcFrom: GPUChecker, pi: DryProgramInfo): void {
    }
   

}

